import { useState, type FormEvent } from 'react';
import { useRessource, messageErreur } from '../../api/ressource';
import { api, ErreurApi } from '../../api/client';
import { useAuth } from '../../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../../composants/Tableau';
import { BandeauAlerte } from '../../composants/BandeauAlerte';
import { Champ } from '../../composants/Champ';
import { Pastille } from '../../composants/Pastille';
import { Modale } from '../../composants/Modale';
import { Icone } from '../../composants/Icone';

interface Agence {
  id: string;
  code: string;
  libelle: string;
  ville: string | null;
  actif: boolean;
}

const VIDE = { code: '', libelle: '', ville: '', actif: true };

export function Agences() {
  const { aRole } = useAuth();
  const { donnees, chargement, erreur, recharger } =
    useRessource<Agence[]>('/agences?inactives=true');
  const agences = donnees ?? [];
  const estAdmin = aRole('ADMIN');

  // `edition` : null = fermé, sinon l'id édité ('' pour une création).
  const [edition, setEdition] = useState<string | null>(null);
  const [saisie, setSaisie] = useState(VIDE);
  const [erreurForm, setErreurForm] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const enCreation = edition === '';

  function ouvrirCreation() {
    setSaisie(VIDE);
    setEdition('');
    setErreurForm(null);
  }
  function ouvrirEdition(a: Agence) {
    setSaisie({ code: a.code, libelle: a.libelle, ville: a.ville ?? '', actif: a.actif });
    setEdition(a.id);
    setErreurForm(null);
  }
  function fermer() {
    setEdition(null);
  }

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreurForm(null);
    setOccupe(true);
    try {
      const corps = {
        code: saisie.code,
        libelle: saisie.libelle,
        ville: saisie.ville || undefined,
      };
      if (enCreation) {
        await api.post('/agences', corps);
      } else {
        await api.patch(`/agences/${edition}`, { ...corps, actif: saisie.actif });
      }
      fermer();
      await recharger();
    } catch (e) {
      setErreurForm(e instanceof ErreurApi ? messageErreur(e) : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Agence>[] = [
    { cle: 'code', entete: 'Code', num: true, rendu: (a) => a.code },
    { cle: 'libelle', entete: 'Agence', rendu: (a) => a.libelle },
    { cle: 'ville', entete: 'Ville', rendu: (a) => a.ville ?? '—' },
    {
      cle: 'actif',
      entete: 'État',
      rendu: (a) =>
        a.actif ? (
          <Pastille ton="succes">Active</Pastille>
        ) : (
          <Pastille ton="neutre">Inactive</Pastille>
        ),
    },
    ...(estAdmin
      ? [
          {
            cle: 'actions',
            entete: '',
            num: true,
            rendu: (a: Agence) => (
              <button
                type="button"
                className="tableau__action"
                onClick={() => ouvrirEdition(a)}
                aria-label={`Éditer ${a.libelle}`}
              >
                <Icone nom="editer" taille={16} />
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <p className="entete__fil">Référentiels</p>
      <div className="entete">
        <h1>Agences</h1>
        <span className="entete__ref">{agences.length} agences</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      <Bloc
        titre="Réseau"
        meta={chargement ? 'chargement…' : undefined}
        actions={
          estAdmin && (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={ouvrirCreation}
            >
              <Icone nom="creer" taille={15} />
              Nouvelle agence
            </button>
          )
        }
      >
        <Tableau colonnes={colonnes} lignes={agences} cle={(a) => a.id} />
      </Bloc>

      <Modale
        ouverte={edition !== null}
        onFermer={fermer}
        surtitre="Agence"
        titre={enCreation ? 'Nouvelle agence' : 'Modifier l’agence'}
      >
        {erreurForm && (
          <BandeauAlerte nature="refus" titre="Enregistrement refusé">
            {erreurForm}
          </BandeauAlerte>
        )}
        <form onSubmit={(e) => void enregistrer(e)} noValidate>
          <Champ
            label="Code"
            value={saisie.code}
            onChange={(e) => setSaisie({ ...saisie, code: e.target.value })}
            maxLength={20}
            required
          />
          <Champ
            label="Libellé"
            value={saisie.libelle}
            onChange={(e) => setSaisie({ ...saisie, libelle: e.target.value })}
            maxLength={120}
            required
          />
          <Champ
            label="Ville"
            value={saisie.ville}
            onChange={(e) => setSaisie({ ...saisie, ville: e.target.value })}
          />
          {!enCreation && (
            <label className="case-a-cocher">
              <input
                type="checkbox"
                checked={saisie.actif}
                onChange={(e) => setSaisie({ ...saisie, actif: e.target.checked })}
              />
              Agence active
            </label>
          )}
          <div className="modale__actions">
            <button type="button" className="bouton bouton--fantome" onClick={fermer}>
              Annuler
            </button>
            <button
              className="bouton"
              type="submit"
              disabled={occupe || !saisie.code.trim() || !saisie.libelle.trim()}
            >
              {occupe ? 'Enregistrement…' : enCreation ? 'Créer l’agence' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
